<script setup lang="ts">
import { useForm } from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3'
import { ChevronDown, FileSignature, Upload } from 'lucide-vue-next'
import { onMounted, ref } from 'vue'

import AppDialog from '@/components/AppDialog.vue'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { requestSignLoanApplicationDocumentsPath, signTemplatesLoanApplicationDocumentsPath } from '@/routes'

interface Template {
  name: string
  templateId: string
}

interface Document {
  name: string
  status: string
}

const props = defineProps<{
  applicationId: string
  documents: Document[]
}>()

// ── Templates ────────────────────────────────────────────────────────────────

const templates = ref<Template[]>([])
const templatesLoading = ref(false)
const templatesError = ref<string | null>(null)

onMounted(async () => {
  templatesLoading.value = true
  try {
    const res = await fetch(signTemplatesLoanApplicationDocumentsPath(props.applicationId))
    const data = await res.json()
    if (!res.ok) {
      templatesError.value = data.error ?? 'Failed to load templates'
    } else {
      templates.value = data.templates ?? []
    }
  } catch {
    templatesError.value = 'Failed to load templates'
  } finally {
    templatesLoading.value = false
  }
})

function isTemplateSent(template: Template): boolean {
  const normalized = template.name.toLowerCase().split(' ').join('_')
  return props.documents.some(
    (d) => d.name === normalized && (d.status === 'sign_requested' || d.status === 'signed'),
  )
}

function sendTemplate(template: Template) {
  if (isTemplateSent(template)) return
  router.post(
    requestSignLoanApplicationDocumentsPath(props.applicationId),
    { name: template.name, template_id: template.templateId },
    { preserveScroll: true },
  )
}

// ── Upload PDF dialog ─────────────────────────────────────────────────────────

const uploadOpen = ref(false)
const uploadFileError = ref<string | null>(null)
const uploadForm = useForm({ name: '', document: null as File | null })

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  uploadForm.document = null
  uploadFileError.value = null
  if (!file) return
  if (file.type !== 'application/pdf') {
    uploadFileError.value = 'Only PDF files are accepted.'
    input.value = ''
    return
  }
  if (file.size > 10 * 1024 * 1024) {
    uploadFileError.value = 'File is too large. Maximum size is 10 MB.'
    input.value = ''
    return
  }
  uploadForm.document = file
}

function submitUpload() {
  uploadForm.post(requestSignLoanApplicationDocumentsPath(props.applicationId), {
    preserveScroll: true,
    onSuccess: () => {
      uploadOpen.value = false
      uploadForm.reset()
      uploadFileError.value = null
    },
  })
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button size="sm" variant="outline" class="gap-1.5 text-xs" :disabled="templatesLoading">
        <FileSignature class="size-3.5" />
        DocuSign
        <ChevronDown class="size-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <template v-if="templatesError">
        <DropdownMenuItem disabled class="text-destructive text-xs">{{ templatesError }}</DropdownMenuItem>
        <DropdownMenuSeparator />
      </template>
      <template v-else-if="templates.length > 0">
        <DropdownMenuItem
          v-for="template in templates"
          :key="template.templateId"
          :disabled="isTemplateSent(template)"
          @click="sendTemplate(template)"
        >
          {{ template.name }}
          <span v-if="isTemplateSent(template)" class="text-muted-foreground ml-auto pl-2 text-xs">Sent</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
      </template>
      <template v-else-if="!templatesLoading">
        <DropdownMenuItem disabled class="text-muted-foreground text-xs italic">No templates in DocuSign</DropdownMenuItem>
        <DropdownMenuSeparator />
      </template>
      <DropdownMenuItem @click="uploadOpen = true">
        <Upload class="mr-1.5 size-3.5" />
        Upload PDF to sign…
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>

  <AppDialog
    v-model:open="uploadOpen"
    title="Send PDF for Signature"
    description="Upload a PDF document. The customer will receive a DocuSign email to sign it."
    :loading="uploadForm.processing"
    loading-message="Sending to DocuSign…"
    class="sm:max-w-md"
  >
    <div class="space-y-4">
      <p v-if="uploadFileError" class="text-destructive text-xs">{{ uploadFileError }}</p>
      <div class="grid gap-1.5">
        <Label class="text-xs">Document name</Label>
        <Input v-model="uploadForm.name" placeholder="e.g. Loan Agreement" />
      </div>
      <div class="grid gap-1.5">
        <Label class="text-xs">PDF file (max 10 MB)</Label>
        <Input type="file" accept=".pdf" @change="onFileChange" />
      </div>
    </div>
    <template #footer>
      <Button variant="outline" :disabled="uploadForm.processing" @click="uploadOpen = false">Cancel</Button>
      <Button
        :disabled="!uploadForm.name || !uploadForm.document || !!uploadFileError || uploadForm.processing"
        @click="submitUpload"
      >
        Send for Signature
      </Button>
    </template>
  </AppDialog>
</template>
